import {Fragment, useState} from "react";
import type {ReactNode} from "react";
import {
  CloseOutlined,
  DollarOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import {Alert, Button, Drawer, Modal, Space, Spin, Typography} from "antd";
import {useListCustomerPurchasedSubscriptions} from "../../../../react-query-hooks/customers/subscriptions/list-customer-subscriptions";
import {useGetCustomerPurchasedSubscription} from "../../../../react-query-hooks/customers/subscriptions/get-customer-subscription";
import type {
  CustomerPurchasedSubscriptionDetail,
  CustomerSubscriptionRecentBilling,
} from "../../../../react-query-hooks/customers/subscriptions/get-customer-subscription/types";
import {subscriptionBillingLabel} from "../../../../react-query-hooks/store/subscriptions/constants/billing-enums";
import {useCancelCustomerSubscriptionApi} from "../../../../react-query-hooks/customers/subscriptions/cancel-customer-subscription";
import {useCustomerIdentity} from "../../../../redux/auth/login/customer-identity";
import {SUBSCRIPTION_CANCEL_MODAL_OK_BUTTON_STYLE} from "../../../../react-query-hooks/store/subscriptions/constants/subscription-cancel-modal-button";
import {SUBSCRIPTION_STATUS_LISTING_CANCELLED} from "../../../../react-query-hooks/store/subscriptions/constants/subscription-status-codes";
import type {CustomerPurchasedSubscriptionListItem} from "../../../../react-query-hooks/customers/subscriptions/list-customer-subscriptions/types";
import {FullPageLoader} from "../../../full-page-loader";
import {EmptyState} from "../../../empty-state";
import {CustomPagination} from "../../../pagination";
import {
  formatCurrencyAmount,
  formatCurrencyIsoCodeAndAmount,
} from "../../../../utils/format-currency-amount";
import {
  StyledCustomerDashboardProductCard,
  StyledCustomerDashboardProductCardContent,
} from "../../../products/customer-dashboard/card/card.style";
import {LoadingSpinner} from "../../../icons/icons";
import {
  StyledCustomerReviewBlock,
  StyledCustomerReviewBlockStarsAndDate,
  StyledCustomerReviewBlockStarsAndDateWrapper,
} from "../../../calls/past-calls/past-call-card/customer/styles";
import {SubscriptionDrawerDisplayRate} from "../subscription-drawer-display-rate";
import {useGetCustomerReview} from "../../../../react-query-hooks/reviews/get-reviews";
import type {ReviewOfferingType} from "../../../../react-query-hooks/reviews/types";
import {CustomerSubscriptionReviewFormDrawer} from "../customer-subscription-review-drawer";

const CUSTOMER_SUBSCRIPTION_REVIEW_OFFERING_TYPE: ReviewOfferingType = "subscription";

function stripHtml(html: string) {
  return html.replace(/<[^>]+>/g, "").trim();
}

function statusLabel(status: string | number | undefined) {
  if (status === undefined || status === null || status === "") {
    return "—";
  }
  return String(status);
}

function isPurchasedSubscriptionTerminated(detail: CustomerPurchasedSubscriptionDetail) {
  if (detail.statusName?.trim()) {
    const n = detail.statusName.toLowerCase();
    if (
      n.includes("cancel") ||
      n.includes("inactive") ||
      n.includes("ended") ||
      n.includes("expired")
    ) {
      return true;
    }
  }
  const raw = detail.status;
  if (raw === undefined || raw === null || raw === "") {
    return false;
  }
  if (typeof raw === "number" && Number.isFinite(raw)) {
    return raw === SUBSCRIPTION_STATUS_LISTING_CANCELLED;
  }
  const maybeNum =
    typeof raw === "string" && raw.trim() !== "" ? Number(raw.trim()) : NaN;
  if (Number.isFinite(maybeNum)) {
    return maybeNum === SUBSCRIPTION_STATUS_LISTING_CANCELLED;
  }
  const s = String(raw).toLowerCase();
  return s.includes("cancel") || s.includes("inactive") || s.includes("ended");
}

function shouldShowCancelSubscription(detail: CustomerPurchasedSubscriptionDetail) {
  return !isPurchasedSubscriptionTerminated(detail);
}

/** e.g. "January 12, 2026" */
function formatDate(value: string | undefined) {
  if (!value) {
    return "—";
  }
  const d = Date.parse(value);
  if (Number.isNaN(d)) {
    return value;
  }
  return new Date(d).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

const subscriptionDrawerFieldColor = "#1C1726";

function subscriptionDetailFieldRow(label: string, value: ReactNode) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 12,
        width: "100%",
      }}
    >
      <span style={{color: subscriptionDrawerFieldColor, flex: "0 1 auto"}}>{label}</span>
      <span
        style={{
          color: subscriptionDrawerFieldColor,
          fontWeight: 600,
          textAlign: "right",
          flex: "1 1 0%",
          minWidth: 0,
        }}
      >
        {value}
      </span>
    </div>
  );
}

function subscriptionDisplayName(
  item: Pick<CustomerPurchasedSubscriptionListItem, "subscriptionName" | "name">
) {
  const n = item.subscriptionName?.trim() || item.name?.trim();
  return n || "—";
}

function listItemPriceText(item: CustomerPurchasedSubscriptionListItem) {
  if (item.price == null) {
    return "—";
  }
  if (item.price < 1) {
    return "Free";
  }
  return formatCurrencyAmount(item.price, (item.priceCurrency ?? "USD").trim() || "USD");
}

function detailAmountText(detail: CustomerPurchasedSubscriptionDetail) {
  if (detail.amount == null || detail.amount === "") {
    return "—";
  }
  const n = Number(detail.amount);
  const cur = (detail.amountCurrency ?? "USD").trim() || "USD";
  if (!Number.isFinite(n)) {
    return String(detail.amount);
  }
  return formatCurrencyAmount(n, cur);
}

function detailTotalSpendText(detail: CustomerPurchasedSubscriptionDetail) {
  if (detail.totalSpend == null) {
    return "—";
  }
  const cur = (detail.amountCurrency ?? "USD").trim() || "USD";
  return formatCurrencyAmount(detail.totalSpend, cur);
}

function recentBillingAmountText(billing: CustomerSubscriptionRecentBilling) {
  const n = Number(billing.amount);
  const cur = (billing.currency ?? "USD").trim() || "USD";
  if (!Number.isFinite(n)) {
    return String(billing.amount);
  }
  return formatCurrencyIsoCodeAndAmount(n, cur);
}

function recentBillingStatusLabel(billing: CustomerSubscriptionRecentBilling) {
  return billing.statusName?.trim() || statusLabel(billing.status);
}

function recentBillingStatusStyle(statusName: string) {
  const status = statusName.toLowerCase();
  const isPaid = status === "paid" || status === "successful" || status === "success";
  const isUnpaid = status === "unpaid" || status === "failed" || status === "overdue";

  return {
    color: isPaid ? "#52C41A" : isUnpaid ? "#F5222D" : "#1C1726",
    backgroundColor: isPaid ? "#F6FFED" : isUnpaid ? "#FFF1F0" : "#FAFAFA",
    borderRadius: 4,
    display: "inline-block",
    fontSize: 12,
    fontWeight: 600,
    lineHeight: "20px",
    padding: "0 10px",
  };
}

function subscriptionStatusBadgeStyle(statusName: string) {
  const status = statusName.trim().toLowerCase();
  const isActive = status === "active" || status === "live";
  const isCancelled =
    status === "cancelled" || status === "canceled" || status === "inactive";

  return {
    color: isActive ? "#52C41A" : isCancelled ? "#F5222D" : "#1C1726",
    backgroundColor: isActive ? "#F6FFED" : isCancelled ? "#FFF1F0" : "#FAFAFA",
    border: isActive
      ? "1px solid #B7EB8F"
      : isCancelled
      ? "1px solid #FFA39E"
      : "1px solid #D9D9D9",
    borderRadius: 4,
    display: "inline-block",
    fontSize: 12,
    fontWeight: 600,
    lineHeight: "20px",
    padding: "2px 10px",
  };
}

function detailBillingFrequencyLabel(detail: CustomerPurchasedSubscriptionDetail) {
  if (detail.billingInterval == null || detail.billingDuration == null) {
    return "—";
  }
  return subscriptionBillingLabel(detail.billingInterval, detail.billingDuration);
}

export function CustomerSubscriptionListPage() {
  const [page, setPage] = useState(1);
  const [drawerId, setDrawerId] = useState<number | null>(null);
  const [isReviewDrawerOpen, setIsReviewDrawerOpen] = useState(false);
  const perPage = 5;

  const listQuery = useListCustomerPurchasedSubscriptions(page, perPage);
  const detailQuery = useGetCustomerPurchasedSubscription(drawerId ?? undefined);
  const cancelMutation = useCancelCustomerSubscriptionApi();

  const {
    emailAddress: customerEmail,
    firstName: customerFirstName,
    lastName: customerLastName,
  } = useCustomerIdentity();

  const subscriptions = listQuery.data?.data.subscriptions ?? [];
  const listItemForDrawer =
    drawerId != null ? subscriptions.find((s) => s.id === drawerId) : undefined;
  const detail = detailQuery.data;

  const reviewCatalogSubscriptionId =
    drawerId == null
      ? 0
      : detail?.storeSubscriptionId ?? listItemForDrawer?.storeSubscriptionId ?? 0;

  const getCustomerReview = useGetCustomerReview({
    emailAddress: customerEmail,
    offering_id: reviewCatalogSubscriptionId,
    offering_type: CUSTOMER_SUBSCRIPTION_REVIEW_OFFERING_TYPE,
  });

  function openDrawer(item: CustomerPurchasedSubscriptionListItem) {
    setDrawerId(item.id);
    setIsReviewDrawerOpen(false);
  }

  function closeDrawer() {
    setDrawerId(null);
    setIsReviewDrawerOpen(false);
  }

  function openLeaveReviewDrawer() {
    setIsReviewDrawerOpen(true);
  }

  if (listQuery.isLoading) {
    return <FullPageLoader />;
  }

  if (listQuery.isError) {
    return (
      <Alert
        message="Error"
        description={listQuery.error?.message ?? "Something went wrong."}
        type="error"
        showIcon
      />
    );
  }

  const hasItems = subscriptions.length > 0;

  function confirmCancel(subscriptionId: number) {
    Modal.confirm({
      title: "Confirm subscription cancellation",
      content:
        "Are you sure you want to cancel this subscription? You may lose access at the end of the current billing period unless your coach has agreed otherwise.",
      icon: <ExclamationCircleOutlined style={{color: "#F5222D"}} />,
      okText: "Cancel subscription",
      cancelText: "Exit",
      okType: "primary",
      okButtonProps: {
        size: "small",
        type: "primary",
        loading: cancelMutation.isLoading,
        style: {...SUBSCRIPTION_CANCEL_MODAL_OK_BUTTON_STYLE},
      },
      cancelButtonProps: {
        size: "small",
        type: "ghost",
      },
      onOk: () =>
        cancelMutation.mutateAsync({subscriptionId}).then(() => {
          closeDrawer();
        }),
    });
  }

  const drawerTitleName = detail
    ? subscriptionDisplayName(detail)
    : listItemForDrawer
    ? subscriptionDisplayName(listItemForDrawer)
    : "Subscription";

  return (
    <div>
      {hasItems ? (
        <>
          <Spin
            spinning={listQuery.isFetching}
            tip="Updating..."
            indicator={LoadingSpinner}
          >
            <Space direction="vertical" size={40} style={{display: "flex"}}>
              {subscriptions.map((item) => {
                const title = subscriptionDisplayName(item);
                const descriptionPlain = stripHtml(item.description ?? "");

                return (
                  <StyledCustomerDashboardProductCard key={item.id} title={title}>
                    <StyledCustomerDashboardProductCardContent>
                      <div className="product-card-image-wrapper">
                        <img
                          className="product-card-image"
                          src={
                            item.coverImage
                              ? item.coverImage
                              : "/images/no-image-placeholder.png"
                          }
                          alt={title}
                        />
                      </div>
                      <p className="product-card-info-text">{descriptionPlain}</p>
                      <div className="product-card-extra-info">
                        <div>
                          <Space size={10}>
                            <DollarOutlined style={{color: "#222222"}} />
                            <span>{listItemPriceText(item)}</span>
                          </Space>
                        </div>
                      </div>
                      <div className="product-card-start-btn">
                        <Button
                          type="primary"
                          size="small"
                          onClick={() => openDrawer(item)}
                        >
                          View details
                        </Button>
                      </div>
                    </StyledCustomerDashboardProductCardContent>
                  </StyledCustomerDashboardProductCard>
                );
              })}
            </Space>
          </Spin>
          <CustomPagination
            isPreviousData={listQuery.isPreviousData}
            page={page}
            paginationMeta={listQuery.data?.meta}
            setPage={setPage}
          />
        </>
      ) : (
        <EmptyState message="You do not have any subscriptions at the moment. Subscribe to get started." />
      )}

      <Drawer
        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
            }}
          >
            <Typography.Text
              strong
              style={{fontSize: "1.6rem", lineHeight: 1.3, flex: 1, minWidth: 0}}
              ellipsis
            >
              {drawerTitleName}
            </Typography.Text>
            <Button
              type="text"
              size="small"
              icon={
                <CloseOutlined style={{fontSize: "1.6rem", color: "rgba(0,0,0,0.45)"}} />
              }
              onClick={closeDrawer}
              aria-label="Close"
            />
          </div>
        }
        closable={false}
        placement="right"
        width={580}
        onClose={closeDrawer}
        visible={drawerId !== null}
        destroyOnClose
        bodyStyle={{padding: 16, paddingBottom: 24}}
        footerStyle={{backgroundColor: "#F5F5F5", borderTop: "none", padding: 24}}
        footer={
          detail ? (
            <Space size={14} wrap style={{justifyContent: "flex-start"}}>
              {shouldShowCancelSubscription(detail) ? (
                <Button
                  type="primary"
                  size="small"
                  loading={cancelMutation.isLoading}
                  onClick={() => confirmCancel(detail.id)}
                >
                  Cancel subscription
                </Button>
              ) : null}
              {customerEmail &&
              reviewCatalogSubscriptionId > 0 &&
              !getCustomerReview.isSuccess ? (
                <Button type="primary" ghost size="small" onClick={openLeaveReviewDrawer}>
                  Leave a review
                </Button>
              ) : null}
            </Space>
          ) : null
        }
      >
        {drawerId !== null && detailQuery.isLoading ? (
          <div style={{padding: 48, textAlign: "center"}}>
            <Spin />
          </div>
        ) : detailQuery.isError ? (
          <Alert type="error" message={detailQuery.error?.message ?? "Failed to load"} />
        ) : detail ? (
          <Space direction="vertical" size={24} style={{width: "100%"}}>
            <div
              style={{
                backgroundColor: "#EAE6F3",
                border: "1px solid #1C1726",
                borderRadius: 8,
                padding: 16,
                boxSizing: "border-box",
                width: "100%",
              }}
            >
              <Space direction="vertical" size={16} style={{width: "100%"}}>
                {subscriptionDetailFieldRow(
                  "Subscription name",
                  subscriptionDisplayName(detail)
                )}
                {subscriptionDetailFieldRow("Tier name", detail.tierName?.trim() || "—")}
                {subscriptionDetailFieldRow("Price", detailAmountText(detail))}
                {subscriptionDetailFieldRow(
                  "Status",
                  <span
                    style={subscriptionStatusBadgeStyle(
                      detail.statusName || statusLabel(detail.status)
                    )}
                  >
                    {detail.statusName || statusLabel(detail.status)}
                  </span>
                )}
                {subscriptionDetailFieldRow(
                  "Billing frequency",
                  detailBillingFrequencyLabel(detail)
                )}
                {subscriptionDetailFieldRow(
                  "Start date",
                  formatDate(detail.startDate ?? detail.createdAt)
                )}
                {subscriptionDetailFieldRow(
                  "Next billing date",
                  formatDate(detail.nextBillingDate)
                )}
                {subscriptionDetailFieldRow("Total spend", detailTotalSpendText(detail))}
              </Space>
            </div>

            <div>
              <Typography.Title level={5} style={{marginTop: 0, marginBottom: 16}}>
                Recent billing
              </Typography.Title>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr auto",
                  alignItems: "center",
                  border: "1px solid #F0F0F0",
                  borderRadius: 2,
                  overflow: "hidden",
                }}
              >
                <Typography.Text
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    padding: 12,
                    background: "#FAFAFA",
                  }}
                >
                  Date
                </Typography.Text>
                <Typography.Text
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    padding: 12,
                    background: "#FAFAFA",
                  }}
                >
                  Amount
                </Typography.Text>
                <Typography.Text
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    textAlign: "right",
                    padding: 12,
                    background: "#FAFAFA",
                  }}
                >
                  Status
                </Typography.Text>
                {(detail.recentBillings ?? []).length > 0 ? (
                  (detail.recentBillings ?? []).map((billing) => {
                    const billingStatus = recentBillingStatusLabel(billing);
                    return (
                      <Fragment key={billing.id}>
                        <Typography.Text style={{padding: 12}}>
                          {formatDate(billing.createdAt)}
                        </Typography.Text>
                        <Typography.Text style={{padding: 12}}>
                          {recentBillingAmountText(billing)}
                        </Typography.Text>
                        <div style={{padding: 12, textAlign: "right"}}>
                          <span style={recentBillingStatusStyle(billingStatus)}>
                            {billingStatus}
                          </span>
                        </div>
                      </Fragment>
                    );
                  })
                ) : (
                  <Typography.Text
                    type="secondary"
                    style={{gridColumn: "1 / -1", padding: 12}}
                  >
                    No recent billing yet.
                  </Typography.Text>
                )}
              </div>
            </div>

            {customerEmail && reviewCatalogSubscriptionId > 0 ? (
              <div>
                <Space direction="vertical" style={{display: "flex"}} size={16}>
                  {getCustomerReview.isLoading ? (
                    <div style={{padding: "1rem 0"}}>
                      <Spin size="small" />
                    </div>
                  ) : getCustomerReview.isSuccess && getCustomerReview.data ? (
                    <>
                      <Typography.Title level={5} style={{marginTop: 0, marginBottom: 0}}>
                        Your review
                      </Typography.Title>
                      <StyledCustomerReviewBlock>
                        <Space direction="vertical" style={{display: "flex"}} size={16}>
                          <StyledCustomerReviewBlockStarsAndDateWrapper>
                            <StyledCustomerReviewBlockStarsAndDate>
                              <SubscriptionDrawerDisplayRate
                                disabled
                                defaultValue={getCustomerReview.data.rating}
                                value={getCustomerReview.data.rating}
                                style={{color: "#99C78F"}}
                                allowHalf
                              />
                              <p>
                                {new Date(
                                  getCustomerReview.data.updatedAt
                                ).toDateString()}
                              </p>
                            </StyledCustomerReviewBlockStarsAndDate>
                            <Button
                              type="text"
                              size="small"
                              icon={<EditOutlined style={{fontSize: "1.6rem"}} />}
                              onClick={openLeaveReviewDrawer}
                            />
                          </StyledCustomerReviewBlockStarsAndDateWrapper>
                          <p>{getCustomerReview.data.review}</p>
                        </Space>
                      </StyledCustomerReviewBlock>
                    </>
                  ) : null}
                </Space>
              </div>
            ) : null}
          </Space>
        ) : null}
      </Drawer>

      {drawerId !== null && reviewCatalogSubscriptionId > 0 ? (
        <CustomerSubscriptionReviewFormDrawer
          title={drawerTitleName}
          isVisible={isReviewDrawerOpen}
          onClose={() => setIsReviewDrawerOpen(false)}
          offeringId={reviewCatalogSubscriptionId}
          customerEmail={customerEmail}
          customerFirstName={customerFirstName}
          customerLastName={customerLastName}
        />
      ) : null}
    </div>
  );
}
